import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';

import { Tooltip } from '@/components/tooltip/tooltip';
import { useModelsQuery } from '@/hooks/queries/useModelsQuery';

import { KeyValueRows } from './KeyValueRows';
import styles from './modalMetricCreate.module.scss';
import { MetricFormData } from './schemas';

type Props = {
  disabled?: boolean;
};

// Sentinel value for "None" / use the model's built-in default temperature.
// Serialized to the string 'default' on submit.
const TEMP_NONE = -0.1;

/** LLM-as-a-Judge configuration fields (model, temperature, prompt, params). */
export const MetricLlmConfigFields: React.FC<Props> = ( { disabled } ) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext<MetricFormData>();
  const { data: models, error: modelsError } = useModelsQuery( { enabled: true } );
  const [ autoSnapped, setAutoSnapped ] = useState( false );

  const model = watch( 'config.model' );
  const temperature = watch( 'config.temperature' ) ?? 0;
  const configErrors = errors.config as { model?: { message?: string }; prompt?: { message?: string } } | undefined;

  useEffect( () => {
    if ( modelsError ) {
      console.error( 'unable to get models' );
      toast( 'unable to get models' );
    }
  }, [ modelsError ] );

  // When the selected model doesn't support temperature, snap to the
  // "None"/default sentinel and lock the slider.
  useEffect( () => {
    const selected = models?.find( ( m ) => m.name === model );
    if ( !selected?.supportedParams ) return;
    if ( !selected.supportedParams.includes( 'temperature' ) ) {
      setValue( 'config.temperature', TEMP_NONE, { shouldDirty: true } );
      setAutoSnapped( true );
    } else {
      setAutoSnapped( false );
    }
  }, [ model, models, setValue ] );

  return (
    <>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <div className="layout__col">
          <label className={ clsx( 'auth_flow_label', configErrors?.model && 'required' ) } htmlFor="config.model">
            LLM <span className={ styles.required_asterisk }>*</span>
          </label>
          <select
            id="config.model"
            className="auth_flow_input"
            disabled={ disabled }
            data-testid="llm-select"
            { ...register( 'config.model' ) }
          >
            <option value="">-</option>
            {( models ?? [] ).map( ( m ) => (
              <option key={ m.name } value={ m.name }>{m.name}</option>
            ) )}
          </select>
          {configErrors?.model && <div className={ styles.error_message }>{configErrors.model.message}</div>}
        </div>
        <div className={ clsx( 'layout__col', styles.temperature ) }>
          <label htmlFor="config.temperature">
            Temperature:{' '}
            <span data-testid="temperature-readout">{temperature < 0 ? 'None' : temperature}</span>
            <Tooltip
              content="This model uses its built-in default temperature."
              position="top"
              disabled={ !autoSnapped }
            >
              <FiInfo
                className={ clsx( styles.temp_info, { [ styles.temp_info_hidden ]: !autoSnapped } ) }
                aria-hidden={ !autoSnapped }
                data-testid="temperature-auto-info"
              />
            </Tooltip>
          </label>
          <input
            id="config.temperature"
            type="range"
            min="-0.1"
            max="1"
            step="0.1"
            value={ temperature }
            disabled={ disabled || autoSnapped }
            onChange={ ( e ) => setValue( 'config.temperature', Number( e.target.value ), { shouldDirty: true } ) }
            data-testid="temperature-slider"
          />
        </div>
      </div>

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label className={ clsx( 'auth_flow_label', configErrors?.prompt && 'required' ) } htmlFor="config.prompt">
          Grading Prompt <span className={ styles.required_asterisk }>*</span>
        </label>
        <textarea
          id="config.prompt"
          className={ styles.prompt_textarea }
          rows={ 5 }
          disabled={ disabled }
          data-testid="grading-prompt-input"
          { ...register( 'config.prompt' ) }
        />
        {configErrors?.prompt && <div className={ styles.error_message }>{configErrors.prompt.message}</div>}
      </div>

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label className="auth_flow_label">Prompt Parameters</label>
        <KeyValueRows
          name="config.promptParameters"
          keyField="key"
          valueField="value"
          keyLabel="Key"
          valueLabel="Value"
          addLabel="Add parameter"
          testIdPrefix="prompt-param"
          disabled={ disabled }
        />
      </div>
    </>
  );
};
